import type { UiNode } from "@agent/uiSchema";
import { apiClient } from "@api/client";
import { request, requestVoid } from "@api/request";
import type { components } from "@api/schema";
import { type CardSeverity, toCardSeverity } from "@subjects/cardSeverity";
import type { SubjectKind } from "@subjects/subjectsClient";
import { toTask, type Task } from "@tasks/tasksClient";

/**
 * A persistent, agent-composed SavedView (BE-1862): a named, read-only a2ui
 * dashboard linked to one or more subjects. The frontend only reads and deletes —
 * views are composed by the agent in chat and carry no submission flow (no
 * `render_id`, no PATCH).
 *
 * `surfacedPosition`, `severity` and `reason` are the agent's per-subject curation
 * (BE-1909 / BE-1918) and are meaningful ONLY off `surfaced()`: position is the
 * ascending slot order, severity is `calm`/`watch`/`urgent` with `reason` the
 * one-line why. Everywhere else position is null and severity `calm`.
 */
/**
 * A view's sign-off lifecycle (BE-2034), denormalized onto the view: `open` is
 * live-updating and edited in place; `signed` locks it (edits supersede).
 */
export type ViewStatus = "open" | "signed";

/**
 * One audience the agent aimed a view at, and when it went up for them (BE-2151).
 * Both identity fields null is the generic "everyone" audience — which is every
 * entry today, since the agent sets no audience yet (BE-2145).
 */
export interface Audience {
  userId: string | null;
  systemRole: components["schemas"]["SystemRole"] | null;
  surfacedAt: string;
}

/**
 * One person the system *served* a view to (BE-2151) — not proof anyone read it:
 * the payload reached them, and the UI may claim only that.
 *
 * `secondsToFirstSeen` is measured from the surfacing whose audience they match.
 * Null when they matched none — they opened the view directly, so it was never put
 * in front of them and there is no latency to state. Negative when they were
 * served it before the agent surfaced it.
 */
export interface ViewerAttention {
  userId: string;
  /**
   * The audience scope that delivered the view to them, null when it reached them
   * only as themselves / as everyone / on its own page (BE-2279). Mirrors brian's
   * `ViewerAttentionOut.system_role`; not their own role, since they hold a set.
   */
  systemRole: components["schemas"]["SystemRole"] | null;
  firstSeenAt: string;
  secondsToFirstSeen: number | null;
}

/**
 * One surfaced view's intended audiences and actual readers (BE-2151).
 *
 * An empty `viewers` with a null `firstSeenAt` is the finding — but it says
 * nothing about *why*. `audiences` and `leadershipOnly` are the why: a view aimed
 * at a role nobody in that role opened, or hidden from non-leadership staff, is a
 * routing or visibility finding rather than staff ignoring their work.
 *
 * View-level `surfacedAt`/`secondsToFirstSeen` span every audience; each viewer is
 * timed against their own.
 */
export interface ViewAttention {
  viewId: string;
  title: string;
  /** RLS-hidden from non-leadership staff, so they were never eligible to see it. */
  leadershipOnly: boolean;
  surfacedAt: string;
  audiences: Audience[];
  firstSeenAt: string | null;
  secondsToFirstSeen: number | null;
  viewers: ViewerAttention[];
}

/** One entry in a view's sign-off audit trail (BE-2034). `action` is `signed`. */
export interface SignoffEvent {
  id: string;
  action: string;
  userId: string | null;
  reason: string | null;
  createdAt: string;
}

/**
 * One structured row of a row-based view — the estimate line items (BE-2035).
 * `[]` for view types without rows. These rows are the only source of the line
 * items and total; `root` is notes prose only, so the FE renders and totals the
 * rows itself (open and signed alike). Decimal fields are JSON strings.
 */
export interface ViewItem {
  id: string;
  name: string;
  quantity: string;
  unit: string | null;
  unitPrice: string | null;
  pimsRef: string | null;
  createdAt: string;
}

/**
 * One subject a view is about (BE-2395). Mirrors brian's `ViewSubjectOut`; the
 * shape matches `Subject` so the search palette can render either through the
 * same row.
 */
export interface ViewSubject {
  id: string;
  kind: SubjectKind;
  name: string | null;
  clientName: string | null;
}

export interface View {
  id: string;
  /**
   * The KIND of document ("Medical Note"), NOT who it is about (BE-2395). Views
   * of a kind deliberately share it, so a name alone does not identify one —
   * pair it with `subtitle`, and with `subjects` wherever the surrounding UI
   * does not already say whose view this is (see `viewCaption`).
   */
  name: string;
  /** The one line telling this view apart from others of its kind ("July 12
   * wellness visit"). Null on documents a subject only ever has one of. */
  subtitle: string | null;
  /** Who/what the view is about; `[]` only if every linked subject vanished. */
  subjects: ViewSubject[];
  root: UiNode;
  data: Record<string, unknown>;
  surfacedPosition: number | null;
  severity: CardSeverity;
  reason: string | null;
  status: ViewStatus;
  /** Non-null once this version was replaced by an edited successor. */
  supersededBy: string | null;
  signedByUserId: string | null;
  signedAt: string | null;
  /** Open tasks pointing at this view via `task_views` (BE-2034) — the compact card count. */
  openTaskCount: number;
  /** Structured rows for row-based views (estimates, BE-2035); `[]` otherwise. */
  items: ViewItem[];
  createdAt: string;
  updatedAt: string;
}

export const toView = (v: components["schemas"]["ViewOut"]): View => ({
  id: v.uuid,
  name: v.title,
  subtitle: v.subtitle ?? null,
  // Defaulted, not just typed: `subjects` is required because BE-2395 says so,
  // and a frontend reaching an older brian (deploy order, a rollback, a dev on
  // a stale API) would throw here and take out every surface that maps a view.
  subjects: (v.subjects ?? []).map((s) => ({
    id: s.entity_uuid,
    kind: s.kind,
    name: s.name,
    clientName: s.client_name ?? null,
  })),
  root: v.root as UiNode,
  data: (v.data as Record<string, unknown> | null) ?? {},
  surfacedPosition: v.surfaced_position ?? null,
  severity: toCardSeverity(v.severity),
  reason: v.reason ?? null,
  status: v.status as ViewStatus,
  supersededBy: v.superseded_by ?? null,
  signedByUserId: v.signed_by_user_uuid ?? null,
  signedAt: v.signed_at ?? null,
  openTaskCount: v.open_task_count,
  items: v.items.map((i) => ({
    id: i.uuid,
    name: i.name,
    quantity: i.quantity,
    unit: i.unit,
    unitPrice: i.unit_price,
    pimsRef: i.pims_ref,
    createdAt: i.created_at,
  })),
  createdAt: v.created_at,
  updatedAt: v.updated_at,
});

export const viewsClient = {
  list: (subject?: string): Promise<View[]> =>
    request(() =>
      apiClient.GET("/views", {
        params: { query: subject ? { subject } : {} },
      }),
    ).then((rows) => rows.map(toView)),

  /** The subject's agent-surfaced views, in surfaced order (BE-1909). */
  surfaced: (subject: string): Promise<View[]> =>
    request(() =>
      apiClient.GET("/views/surfaced/{entity_uuid}", {
        params: { path: { entity_uuid: subject } },
      }),
    ).then((rows) => rows.map(toView)),

  get: (id: string): Promise<View> =>
    request(() =>
      apiClient.GET("/views/{view_uuid}", {
        params: { path: { view_uuid: id } },
      }),
    ).then(toView),

  remove: (id: string): Promise<void> =>
    requestVoid(() =>
      apiClient.DELETE("/views/{view_uuid}", {
        params: { path: { view_uuid: id } },
      }),
    ),

  /**
   * Sign an open view (BE-2034) — a human-only action stamped with the acting
   * user, which the portal always is (the app is user-scoped). Flips the view to
   * `signed` and auto-closes its open sign task.
   */
  sign: (id: string): Promise<View> =>
    request(() =>
      apiClient.POST("/views/{view_uuid}/sign", {
        params: { path: { view_uuid: id } },
        body: {},
      }),
    ).then(toView),

  /**
   * Remove one structured row from an open view (BE-2035) — per-item Remove on
   * an estimate. Returns the updated view with the shrunken `items`, so no
   * follow-up fetch is needed. 409 if the view is already signed.
   */
  removeItem: (viewId: string, itemId: string): Promise<View> =>
    request(() =>
      apiClient.DELETE("/views/{view_uuid}/items/{item_uuid}", {
        params: { path: { view_uuid: viewId, item_uuid: itemId } },
      }),
    ).then(toView),

  /**
   * Relay staff feedback on an open view to the clinic agent (BE-2035), which
   * reworks the items in the background; the view then re-renders in place via
   * the usual socket update. 409 if the view is signed/superseded.
   */
  requestChanges: (viewId: string, note: string): Promise<void> =>
    requestVoid(() =>
      apiClient.POST("/views/{view_uuid}/request-changes", {
        params: { path: { view_uuid: viewId } },
        body: { note },
      }),
    ),

  /** The open tasks pointing at this view via the `task_views` join (BE-2034). */
  tasks: (id: string): Promise<Task[]> =>
    request(() =>
      apiClient.GET("/views/{view_uuid}/tasks", {
        params: { path: { view_uuid: id } },
      }),
    ).then((rows) => rows.map(toTask)),

  /** This view's sign-off audit trail, oldest first (BE-2034). */
  signoffs: (id: string): Promise<SignoffEvent[]> =>
    request(() =>
      apiClient.GET("/views/{view_uuid}/signoffs", {
        params: { path: { view_uuid: id } },
      }),
    ).then((rows) =>
      rows.map((e) => ({
        id: e.uuid,
        action: e.action,
        userId: e.user_uuid,
        reason: e.reason,
        createdAt: e.created_at,
      })),
    ),

  /**
   * Surfaced-to-seen report for one entity, oldest surfacing first (BE-2151).
   * Leadership-only — 403 for anyone else, so callers gate the entry point with
   * `canViewAttention` rather than letting the request fail.
   *
   * Scoped to views surfaced *on this entity*: a view surfaced on the visit's
   * patient or a stage is reported by asking for that uuid, so a visit-level
   * picture fans out (see `useVisitAttention`).
   */
  attention: (entityUuid: string): Promise<ViewAttention[]> =>
    request(() =>
      apiClient.GET("/views/attention/{entity_uuid}", {
        params: { path: { entity_uuid: entityUuid } },
      }),
    ).then((rows) =>
      rows.map((r) => ({
        viewId: r.view_uuid,
        title: r.title,
        leadershipOnly: r.leadership_only,
        surfacedAt: r.surfaced_at,
        audiences: r.audiences.map((a) => ({
          userId: a.user_uuid,
          systemRole: a.system_role,
          surfacedAt: a.surfaced_at,
        })),
        firstSeenAt: r.first_seen_at,
        secondsToFirstSeen: r.seconds_to_first_seen,
        viewers: r.viewers.map((v) => ({
          userId: v.user_uuid,
          systemRole: v.system_role,
          firstSeenAt: v.first_seen_at,
          secondsToFirstSeen: v.seconds_to_first_seen,
        })),
      })),
    ),
};
